import React, { useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffAttApi, errMsg, fmtTime, todayIST } from '@/services/staffAttendanceApi';

const yday = () => { const d = new Date(`${todayIST()}T12:00:00`); d.setDate(d.getDate() - 1); return d.toLocaleDateString('en-CA'); };
const TILES = [['staff_days', 'Staff-days with eSSL'], ['changed_days', 'Staff-days to change'], ['filled_days', 'No app punch → filled'],
  ['in_only', '…of which In only'], ['in_earlier', 'In → earlier eSSL'], ['out_later', 'Out → later eSSL'], ['out_added', 'Missing Out filled'],
  ['punches_added', 'Punches added'], ['punches_updated', 'Punches updated']];
const fmtD = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const Summary = ({ r }) => (
  <div className="space-y-3" data-testid="essl-backfill-summary">
    <div className="grid grid-cols-3 gap-2">
      {TILES.map(([k, l]) => <div key={k} className="rounded-lg border bg-gray-50 px-3 py-2"><div className="text-[11px] text-gray-500">{l}</div><div className="text-lg font-bold tabular-nums" data-testid={`essl-backfill-${k}`}>{r[k]}</div></div>)}
    </div>
    {r.skipped_today && <div className="text-xs text-gray-500">Today is left out — only closed days are changed (up to {fmtD(r.date_to)}).</div>}
    {r.unmatched_count > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="essl-backfill-unmatched">
      Skipped {r.unmatched_count} eSSL code(s) not in Staff Master: {r.unmatched.slice(0, 30).map((u) => `${u.code}${u.name && u.name !== u.code ? ` ${u.name}` : ''} (${u.days} day${u.days === 1 ? '' : 's'})`).join(', ')}{r.unmatched_count > 30 ? ' …' : ''}</div>}
    {r.changes.length > 0 && (
      <div className="max-h-64 overflow-y-auto rounded-lg border text-xs" data-testid="essl-backfill-changes">
        <table className="w-full"><tbody>
          {r.changes.map((c, i) => (
            <tr key={i} className="border-b last:border-0"><td className="px-2 py-1 whitespace-nowrap">{fmtD(c.date)}</td><td className="px-2 py-1">{c.staff_name} <span className="text-gray-400">{c.staff_id}</span></td>
              <td className="px-2 py-1">{c.label}</td><td className="px-2 py-1 font-mono whitespace-nowrap">{c.from ? `${fmtTime(c.from)} → ` : ''}{fmtTime(c.to)}</td></tr>
          ))}
        </tbody></table>
        {r.changes_total > r.changes.length && <div className="px-2 py-1 text-gray-500">Showing the first {r.changes.length} of {r.changes_total} changes</div>}
      </div>
    )}
  </div>
);

/** HR: preview, then apply the eSSL final In / Out to every staff-day in a date range. */
export const EsslBackfill = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => ({ date_from: `${yday().slice(0, 8)}01`, date_to: yday() }));
  const [prev, setPrev] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = (apply) => {
    setBusy(true);
    staffAttApi.esslBackfill({ ...f, apply }).then((r) => {
      if (apply) { setDone(r); setPrev(null); toast.success(`eSSL backfill done — ${r.changed_days} staff-day(s) updated`); onDone(); } else { setPrev(r); setDone(null); }
    }).catch((e) => toast.error(errMsg(e, 'Backfill failed'))).finally(() => setBusy(false));
  };
  const edit = (k, v) => { setF({ ...f, [k]: v }); setPrev(null); setDone(null); };
  return (
    <>
      <Button variant="outline" className="bg-white" onClick={() => setOpen(true)} data-testid="essl-backfill-btn"><History className="w-4 h-4 mr-1.5" />Backfill from eSSL</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="essl-backfill-dialog">
          <DialogHeader><DialogTitle>Backfill attendance from eSSL</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-600">For every staff-day with eSSL punches: no app punch → In (and Out) from the eSSL first / last punch; otherwise the final In is the <b>earlier</b> of app / eSSL and the final Out the <b>later</b> one. The original app time is kept in the punch history. Late, Early leave and Hours then use the final times everywhere. Closed days only (before today).</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={f.date_from} onChange={(e) => edit('date_from', e.target.value)} className="w-[160px]" data-testid="essl-backfill-from" />
            <span className="text-sm text-gray-500">to</span>
            <Input type="date" value={f.date_to} onChange={(e) => edit('date_to', e.target.value)} className="w-[160px]" data-testid="essl-backfill-to" />
            <Button variant="outline" onClick={() => run(false)} disabled={busy || !f.date_from || !f.date_to} data-testid="essl-backfill-preview-btn">
              {busy && !prev ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Preview</Button>
            {prev && <Button onClick={() => run(true)} disabled={busy || !prev.changed_days} className="bg-amber-600 hover:bg-amber-700" data-testid="essl-backfill-apply-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Confirm — update {prev.changed_days} staff-day(s)</Button>}
          </div>
          {prev && <Summary r={prev} />}
          {done && <div className="space-y-2"><div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="essl-backfill-done">Done — {done.changed_days} staff-day(s) updated ({done.punches_added} punches added, {done.punches_updated} updated). One-punch days are in Missed Check-outs.</div><Summary r={done} /></div>}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EsslBackfill;
